import { useMemo, useState, type FormEvent } from "react";
import useSWR from "swr";

import {
  api,
  type DelegateCandidate,
  type DelegationRow,
  type Me,
} from "../services/api";

/**
 * Marker stamped into the delegation note so the Out of office toggle can
 * recognise the delegation it created. Out of office is purely a convenience
 * wrapper over the existing delegation API: turning it on creates a delegation
 * starting today, turning it off revokes that delegation.
 */
const OOO_NOTE_MARKER = "Out of office";

/**
 * The far-future default end date offered when a user flips Out of office on
 * without picking their own return date. Delegation requires an end_date, so we
 * supply a sensible long horizon rather than leaving the field empty.
 */
function defaultOooEndDate(): string {
  const d = new Date();
  d.setFullYear(d.getFullYear() + 1);
  return d.toISOString().slice(0, 10);
}

/**
 * A delegation counts as the active Out of office delegation when it carries the
 * Out of office marker, has not been revoked, and today falls within its window.
 */
function isActiveOoo(row: DelegationRow): boolean {
  if (row.revoked_at) return false;
  if (!row.note || !row.note.startsWith(OOO_NOTE_MARKER)) return false;
  const now = Date.now();
  const start = new Date(row.start_date).getTime();
  const end = new Date(row.end_date).getTime();
  return now >= start && now <= end;
}

/**
 * Self-service delegation page.
 *
 * Approval Engine Spec v1.0 §7. A user can set a delegate to receive
 * approval requests assigned to them between start_date and end_date.
 * Multiple approvers can delegate to the same person. Delegation does
 * not chain.
 */
export function MyDelegation() {
  const { data: me } = useSWR<Me>("/api/me", () => api.me());

  const { data: candidates } = useSWR<DelegateCandidate[]>(
    "/api/me/delegations/candidates",
    () => api.delegations.listMyCandidates(),
  );

  const { data, mutate, isLoading, error } = useSWR<DelegationRow[]>(
    "/api/me/delegations",
    () => api.delegations.listMine(),
  );

  // Server-side kill-switch is off (absent = on): the nav link is already
  // hidden, but a saved link can still land here, so render an honest notice
  // rather than a form that the API would reject.
  if (me?.approval_delegation_enabled === false) {
    return (
      <>
        <div className="page-header">
          <h1 className="page-title">Delegation</h1>
        </div>
        <div className="card" style={{ padding: 24 }}>
          <div className="empty-state">
            <p>Delegation is currently disabled for this portal.</p>
            <p className="empty-state-blurb">
              Approvals route to the named approver, who acts on the customer's
              behalf. Contact iQuest if you need delegation enabled.
            </p>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="page-header">
        <h1 className="page-title">Delegation</h1>
        <p className="page-subtitle">
          Route approvals assigned to you to another colleague for a window of
          time. Delegation never chains and never crosses companies.
        </p>
      </div>

      <div className="card" style={{ padding: 24, marginBottom: 24 }}>
        <OutOfOfficeCard
          rows={data ?? []}
          candidates={candidates ?? []}
          loading={isLoading}
          onChanged={async () => {
            await mutate();
          }}
        />
      </div>

      <div className="card" style={{ padding: 24, marginBottom: 24 }}>
        <h2 className="card-title">Set a new delegation</h2>
        <NewDelegationForm
          candidates={candidates ?? []}
          onCreated={async () => {
            await mutate();
          }}
        />
      </div>

      <div className="card" style={{ padding: 24 }}>
        <h2 className="card-title">Your delegations</h2>
        {isLoading && <div className="empty-state">Loading…</div>}
        {error && (
          <div className="form-error">
            {String((error as Error).message ?? error)}
          </div>
        )}
        {!isLoading && !error && (data?.length ?? 0) === 0 && (
          <div className="empty-state">
            <p>No delegations yet.</p>
            <p className="empty-state-blurb">
              Set one above when you are taking leave or stepping back from
              approvals.
            </p>
          </div>
        )}
        {data && data.length > 0 && (
          <DelegationList
            rows={data}
            candidates={candidates ?? []}
            onChanged={async () => {
              await mutate();
            }}
          />
        )}
      </div>
    </>
  );
}

interface OutOfOfficeCardProps {
  rows: DelegationRow[];
  candidates: DelegateCandidate[];
  loading: boolean;
  onChanged: () => void | Promise<void>;
}

/**
 * Out of office toggle. A convenience wrapper over the delegation API:
 * - On: create a delegation starting today, ending on the chosen return date,
 *   noted as Out of office, routing approvals to the chosen backup approver.
 * - Off: revoke the currently active Out of office delegation.
 * State is derived from the delegation list, so it stays in sync with the
 * manual controls below.
 */
function OutOfOfficeCard({
  rows,
  candidates,
  loading,
  onChanged,
}: OutOfOfficeCardProps) {
  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const [backupUserId, setBackupUserId] = useState("");
  const [returnDate, setReturnDate] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const activeOoo = useMemo(() => rows.find(isActiveOoo) ?? null, [rows]);
  const isOut = activeOoo !== null;

  const candidatesById = useMemo(() => {
    const m = new Map<string, DelegateCandidate>();
    for (const c of candidates) m.set(c.id, c);
    return m;
  }, [candidates]);

  const backupName = activeOoo
    ? candidatesById.get(activeOoo.delegate_user_id)?.display_name ??
      activeOoo.delegate_user_id
    : null;

  async function turnOn() {
    if (!backupUserId) {
      setError("Pick a backup approver before going out of office.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.delegations.createMine({
        delegate_user_id: backupUserId,
        start_date: today,
        end_date: returnDate || defaultOooEndDate(),
        note: OOO_NOTE_MARKER,
      });
      setBackupUserId("");
      setReturnDate("");
      await onChanged();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to set out of office",
      );
    } finally {
      setBusy(false);
    }
  }

  async function turnOff() {
    if (!activeOoo) return;
    setBusy(true);
    setError(null);
    try {
      await api.delegations.revokeMine(activeOoo.id);
      await onChanged();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to clear out of office",
      );
    } finally {
      setBusy(false);
    }
  }

  function handleToggle() {
    if (busy || loading) return;
    if (isOut) {
      void turnOff();
    } else {
      void turnOn();
    }
  }

  return (
    <div className="pref-section">
      <div className="pref-row">
        <div className="pref-row-text">
          <div className="pref-row-label">Out of office</div>
          <div className="pref-row-blurb">
            A quick way to route every approval assigned to you to a backup
            colleague while you are away. This creates a delegation under the
            hood, so it shows up in your delegation list below and can be
            revoked there too.
          </div>
        </div>
        <div className="pref-row-control">
          <label
            className={`toggle ${isOut ? "toggle-on" : ""} ${busy || loading ? "toggle-busy" : ""}`}
          >
            <input
              type="checkbox"
              checked={isOut}
              disabled={busy || loading}
              onChange={handleToggle}
              style={{ display: "none" }}
            />
            <span className="toggle-knob" />
          </label>
        </div>
      </div>

      {isOut && activeOoo && (
        <div className="form-info">
          You are currently out of office. While out of office, your approvals
          route to {backupName} until {fmtDate(activeOoo.end_date)}.
        </div>
      )}

      {!isOut && (
        <div className="delegation-form" style={{ marginTop: 12 }}>
          <div className="form-field">
            <label htmlFor="ooo-backup">Backup approver</label>
            <select
              id="ooo-backup"
              value={backupUserId}
              onChange={(e) => setBackupUserId(e.target.value)}
              disabled={busy || loading}
            >
              <option value="">Choose a colleague…</option>
              {candidates.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.display_name} ({c.email})
                </option>
              ))}
            </select>
          </div>

          <div className="form-field">
            <label htmlFor="ooo-return">Return date (optional)</label>
            <input
              id="ooo-return"
              type="date"
              value={returnDate}
              min={today}
              onChange={(e) => setReturnDate(e.target.value)}
              disabled={busy || loading}
            />
          </div>

          <div className="form-field" style={{ alignSelf: "end" }}>
            <button
              type="button"
              className="btn btn-primary"
              disabled={busy || loading || !backupUserId}
              onClick={handleToggle}
            >
              {busy ? "Saving…" : "Go out of office"}
            </button>
          </div>
        </div>
      )}

      {error && (
        <div className="form-error" style={{ marginTop: 12 }}>
          {error}
        </div>
      )}
    </div>
  );
}

interface NewDelegationFormProps {
  candidates: DelegateCandidate[];
  onCreated: () => void | Promise<void>;
}

function NewDelegationForm({ candidates, onCreated }: NewDelegationFormProps) {
  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const [delegateUserId, setDelegateUserId] = useState("");
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!delegateUserId) {
      setError("Pick a delegate.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.delegations.createMine({
        delegate_user_id: delegateUserId,
        start_date: startDate,
        end_date: endDate,
        note: note || null,
      });
      setDelegateUserId("");
      setNote("");
      await onCreated();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create delegation");
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="delegation-form" onSubmit={(e) => void handleSubmit(e)}>
      <div className="form-field">
        <label htmlFor="delegate">Delegate</label>
        <select
          id="delegate"
          value={delegateUserId}
          onChange={(e) => setDelegateUserId(e.target.value)}
          required
        >
          <option value="">Choose someone…</option>
          {candidates.map((c) => (
            <option key={c.id} value={c.id}>
              {c.display_name} ({c.email})
            </option>
          ))}
        </select>
      </div>

      <div className="form-field">
        <label htmlFor="start-date">Start</label>
        <input
          id="start-date"
          type="date"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          required
        />
      </div>

      <div className="form-field">
        <label htmlFor="end-date">End</label>
        <input
          id="end-date"
          type="date"
          value={endDate}
          min={startDate}
          onChange={(e) => setEndDate(e.target.value)}
          required
        />
      </div>

      <div className="form-field" style={{ gridColumn: "1 / -1" }}>
        <label htmlFor="note">Note (optional)</label>
        <input
          id="note"
          type="text"
          value={note}
          maxLength={2000}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Why you are delegating (audit-logged)"
        />
      </div>

      <div style={{ gridColumn: "1 / -1" }}>
        {error && <div className="form-error">{error}</div>}
        <button type="submit" className="btn btn-primary" disabled={busy}>
          {busy ? "Saving…" : "Add delegation"}
        </button>
      </div>
    </form>
  );
}

interface DelegationListProps {
  rows: DelegationRow[];
  candidates: DelegateCandidate[];
  onChanged: () => void | Promise<void>;
}

function classifyDelegation(row: DelegationRow): {
  state: "active" | "scheduled" | "expired" | "revoked";
  label: string;
} {
  if (row.revoked_at) {
    return { state: "revoked", label: "Revoked" };
  }
  const now = Date.now();
  const start = new Date(row.start_date).getTime();
  const end = new Date(row.end_date).getTime();
  if (now < start) return { state: "scheduled", label: "Scheduled" };
  if (now > end) return { state: "expired", label: "Expired" };
  return { state: "active", label: "Active" };
}

function DelegationList({ rows, candidates, onChanged }: DelegationListProps) {
  const byId = useMemo(() => {
    const m = new Map<string, DelegateCandidate>();
    for (const c of candidates) m.set(c.id, c);
    return m;
  }, [candidates]);

  const sorted = useMemo(() => {
    return [...rows].sort((a, b) => b.created_at.localeCompare(a.created_at));
  }, [rows]);

  return (
    <div className="delegation-grid">
      {sorted.map((row) => {
        const target = byId.get(row.delegate_user_id);
        const { state, label } = classifyDelegation(row);
        return (
          <div key={row.id} className="delegation-row">
            <div className="delegation-row-meta">
              <div className="delegation-row-title">
                Delegate to {target?.display_name ?? row.delegate_user_id}
              </div>
              <div className="delegation-row-sub">
                {fmtDate(row.start_date)} → {fmtDate(row.end_date)}
                {row.note ? ` · ${row.note}` : ""}
              </div>
              <div className="delegation-row-sub">
                Set by {row.set_by}
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <span className={`delegation-row-state ${state}`}>{label}</span>
              {state !== "expired" && state !== "revoked" && (
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  onClick={async () => {
                    try {
                      await api.delegations.revokeMine(row.id);
                      await onChanged();
                    } catch (err) {
                      console.warn("revoke failed", err);
                    }
                  }}
                >
                  Revoke
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-AU", {
    dateStyle: "medium",
    timeZone: "Australia/Perth",
  });
}
